import { useTranslation } from '@/lib/i18n';
import type { InventoryHistoryItem } from '@/lib/api-admin/types';
import { DataTable, type DataTableColumn } from '@/components/ui';

interface Props {
  items: ReadonlyArray<InventoryHistoryItem>;
  isLoading?: boolean;
}

export const InventoryHistoryTable = ({ items, isLoading }: Props) => {
  const { t, i18n } = useTranslation('inventory');

  // inventoryId(내부 숫자 PK)·actorUserId(내부 user-id) 컬럼은 운영자에게 의미가 없고
  // 사람친화 대체 필드가 응답에 없어 노출하지 않는다(T7 내부식별자 LOCK).
  const columns: DataTableColumn<InventoryHistoryItem>[] = [
    {
      key: 'type',
      header: t('inventory.history.columns.type'),
      cell: (row) => <span className="text-sm">{t(`inventory.history.type.${row.type}`)}</span>,
    },
    {
      key: 'qtyDelta',
      header: t('inventory.history.columns.qtyDelta'),
      align: 'right',
      cell: (row) => (
        <span
          className="text-sm font-semibold"
          style={{ color: row.qtyDelta > 0 ? 'var(--badge-success-fg)' : 'var(--badge-danger-fg)' }}
        >
          {row.qtyDelta > 0 ? `+${row.qtyDelta}` : row.qtyDelta}
        </span>
      ),
    },
    {
      key: 'qtyAfter',
      header: t('inventory.history.columns.qtyAfter'),
      align: 'right',
      cell: (row) => <span className="text-sm">{row.qtyAfter}</span>,
    },
    {
      key: 'reason',
      header: t('inventory.history.columns.reason'),
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {row.reason ?? '-'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: t('inventory.history.columns.createdAt'),
      cell: (row) => (
        <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
          {new Date(row.createdAt).toLocaleString(i18n.language)}
        </span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" style={{ color: 'var(--content-muted)' }}>
        {t('inventory.table.loading')}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      rows={items as InventoryHistoryItem[]}
      rowKey={(row) => row.historyId}
      emptyState={t('inventory.history.empty')}
      stickyHeader
    />
  );
};
